/**
 * storyBuilder · «Собери User Story» constructor (S04 TD-04 · 8 JUN 2026).
 *
 * A different practice shape from classify-decks (Genie 8 JUN: «не повторять один
 * и тот же элемент… можно построить задачу по-другому — не выбор из двух»). The
 * student ASSEMBLES a User Story from parts: one chip per row (роль / действие /
 * ценность). Each row carries one «story-level» chip + distractors pulled toward
 * Use Case (steps / tech). On «Проверить» each chosen chip gets a verdict + the
 * reason from its own data-fb. Teaches the Story↔Use Case boundary by hand.
 *
 * Also wires the companion «напиши свою» block: clickable `.us-seed` starters drop
 * a skeleton into the `.us-area` textarea so a stuck student has somewhere to begin.
 *
 * Markup contract (authored content · no-JS degradable — without this script the
 * rows render as a readable «parts to choose from» list; nothing is graded):
 *   <div class="usb" data-good="…summary when all correct…" data-mix="…summary when mixed…">
 *     <div class="usb-head"><span class="usb-ttl">Собери User Story</span>
 *       <span class="usb-feat">фича · …</span></div>
 *     <div class="usb-intro">…</div>
 *     <div class="usb-frame">Как <span class="usb-slot" data-slot="0">роль</span>,
 *       я хочу <span class="usb-slot" data-slot="1">действие</span>,
 *       чтобы <span class="usb-slot" data-slot="2">ценность</span>.</div>
 *     <div class="usb-rows">
 *       <div class="usb-row" data-row="0" data-name="роль">
 *         <div class="usb-lab">кто — роль</div>
 *         <div class="usb-chips">
 *           <button type="button" class="usb-chip" data-ok="1" data-fb="…почему верно…">покупатель</button>
 *           <button type="button" class="usb-chip" data-fb="…почему ловушка…">система</button>
 *           …
 *         </div>
 *       </div>
 *       … rows 1, 2 …
 *     </div>
 *     <div class="usb-act"><button type="button" class="usb-check" disabled>Проверить историю</button>
 *       <span class="usb-hint">выбери по одному в каждом ряду</span></div>
 *     <div class="usb-fb" aria-live="polite"></div>
 *   </div>
 *
 *   <div class="us-mine" data-tmpl="Как пользователь, я хочу {feat}, чтобы …">
 *     … <button type="button" class="us-seed" data-feat="войти по отпечатку">вход по отпечатку</button> …
 *     <textarea class="us-area" …></textarea>
 *   </div>
 *
 * `data-ok="1"` marks the story-level chip in a row. `data-fb` is the per-chip
 * explanation (plain text · no backticks). `data-name` labels the row in feedback.
 */

export function initStoryBuilder(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  // ── конструктор «Собери User Story» ──
  const builders = Array.from(root.querySelectorAll<HTMLElement>('.usb'));
  for (const usb of builders) {
    if (usb.dataset.usbInit === '1') continue;
    usb.dataset.usbInit = '1';

    const slots = Array.from(usb.querySelectorAll<HTMLElement>('.usb-slot'));
    const rows = Array.from(usb.querySelectorAll<HTMLElement>('.usb-row'));
    const check = usb.querySelector<HTMLButtonElement>('.usb-check');
    const hint = usb.querySelector<HTMLElement>('.usb-hint');
    const fb = usb.querySelector<HTMLElement>('.usb-fb');
    if (rows.length === 0 || !check || !fb) continue;

    usb.classList.add('usb-ready');

    const picks: Array<HTMLButtonElement | null> = rows.map(() => null);
    const goodTxt =
      usb.dataset.good ??
      'Собрана чистая User Story — намерение пользователя одной строкой, без единого шага.';
    const mixTxt =
      usb.dataset.mix ??
      'Часть выбранного — уровень Use Case (шаги и техника), а не истории. Поправь и попробуй снова.';

    const refreshGate = () => {
      const done = picks.every((p) => p !== null);
      check.disabled = !done;
      if (hint) hint.textContent = done ? 'готово — жми «Проверить»' : 'выбери по одному в каждом ряду';
    };

    for (const row of rows) {
      const r = Number(row.dataset.row ?? 0);
      const chips = Array.from(row.querySelectorAll<HTMLButtonElement>('.usb-chip'));
      for (const chip of chips) {
        const onPick = () => {
          chips.forEach((c) => c.classList.remove('pick'));
          chip.classList.add('pick');
          picks[r] = chip;
          const slot = slots.find((s) => Number(s.dataset.slot ?? -1) === r);
          if (slot) {
            slot.textContent = chip.textContent;
            slot.classList.add('filled');
          }
          refreshGate();
        };
        chip.addEventListener('click', onPick);
        cleanups.push(() => chip.removeEventListener('click', onPick));
      }
    }

    const onCheck = () => {
      let allOk = true;
      const parts: string[] = [];
      rows.forEach((row, r) => {
        const chip = picks[r];
        if (!chip) return;
        const ok = chip.dataset.ok === '1';
        if (!ok) allOk = false;
        const name = row.dataset.name ?? `ряд ${r + 1}`;
        const reason = chip.dataset.fb ?? '';
        parts.push(
          `<div class="usb-fbrow"><span class="usb-fbic ${ok ? 'ok' : 'no'}">${ok ? '✓' : '✕'}</span>` +
            `<span><b>${name}:</b> ${reason}</span></div>`,
        );
      });
      parts.push(
        `<div class="usb-fbsum ${allOk ? 'good' : 'mix'}">${allOk ? goodTxt : mixTxt}</div>`,
      );
      fb.innerHTML = parts.join('');
      fb.classList.add('on');
    };
    check.addEventListener('click', onCheck);
    cleanups.push(() => check.removeEventListener('click', onCheck));
    // Reset guard + ready-class so React 18 StrictMode (dev) re-init rewires after
    // the double-invoke cleanup — otherwise chips/Проверить go dead in dev.
    cleanups.push(() => {
      usb.classList.remove('usb-ready');
      delete usb.dataset.usbInit;
    });

    refreshGate();
  }

  // ── «напиши свою» · затравки → скелет в поле ──
  const mines = Array.from(root.querySelectorAll<HTMLElement>('.us-mine'));
  for (const mine of mines) {
    if (mine.dataset.usmInit === '1') continue;
    mine.dataset.usmInit = '1';

    const area = mine.querySelector<HTMLTextAreaElement>('.us-area');
    const tmpl = mine.dataset.tmpl ?? 'Как пользователь, я хочу {feat}, чтобы …';
    const seeds = Array.from(mine.querySelectorAll<HTMLButtonElement>('.us-seed'));
    if (!area) continue;

    for (const seed of seeds) {
      const onSeed = () => {
        area.value = tmpl.replace('{feat}', seed.dataset.feat ?? '');
        area.focus();
        const end = area.value.length;
        try {
          area.setSelectionRange(end, end);
        } catch {
          /* setSelectionRange unsupported in some test envs · ignore */
        }
      };
      seed.addEventListener('click', onSeed);
      cleanups.push(() => seed.removeEventListener('click', onSeed));
    }
    cleanups.push(() => {
      delete mine.dataset.usmInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
